'use client';
import { useState } from 'react';

const CAL_BOOKING_URL = 'https://cal.com/elevatex/strategy-call';

interface FormData {
  full_name: string;
  email: string;
  instagram_handle: string;
  active_clients: string;
  coaching_niche: string;
}

export default function WaitlistSection() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    full_name: '',
    email: '',
    instagram_handle: '',
    active_clients: '',
    coaching_niche: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (error) setError(null);
  };

  const buildCalUrl = (name: string, email: string): string => {
    const params = new URLSearchParams();
    if (name) params.set('name', name);
    if (email) params.set('email', email);
    const queryString = params.toString();
    return queryString ? `${CAL_BOOKING_URL}?${queryString}` : CAL_BOOKING_URL;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const payload = {
        full_name: formData.full_name,
        email: formData.email,
        instagram_handle: formData.instagram_handle,
        monthly_dm_volume: '',
        what_do_you_want: 'ai-assistants',
        coaching_niche: formData.coaching_niche,
        active_clients: formData.active_clients,
      };

      const response = await fetch('/api/submit-application', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Relay call failed');
      }

      const calUrl = buildCalUrl(formData.full_name, formData.email);
      window.location.href = calUrl;
    } catch (err) {
      console.error('Submission error:', err);
      setError('Something went wrong, please try again');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="waitlist" className="py-24 md:py-32 px-5 md:px-8 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-[420px_1fr] gap-16">
          {/* Left: context */}
          <div>
            <span className="badge badge-coral mb-5">
              <span className="live-dot w-1.5 h-1.5 rounded-full bg-primary inline-block" />
              Start Your 7-Day Guided Pilot
            </span>
            <h2 className="display-md text-foreground mb-5">
              Focus on Coaching.
              <br />
              <span className="gradient-text">Let AI Handle the Backend.</span>
            </h2>
            <p className="text-muted leading-relaxed mb-8">
              Fill in the form and we'll book your strategy call. In 7 days, you'll see ElevateX running with your real clients — fully set up by us.
            </p>

            {/* What happens next */}
            <div className="space-y-4">
              {[
                { step: '01', text: 'Book your strategy call' },
                { step: '02', text: 'We install your AI Assistants in 24 hours' },
                { step: '03', text: 'You see it working with real clients in 7 days' },
              ].map((item) => (
                <div key={item.step} className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary shrink-0">
                    {item.step}
                  </div>
                  <div className="text-sm text-foreground font-medium">{item.text}</div>
                </div>
              ))}
            </div>

            {/* Cal.com info */}
            <div className="mt-8 p-4 rounded-2xl bg-primary/[0.05] border border-primary/10">
              <p className="text-xs text-muted">
                <span className="font-bold text-foreground">After submitting:</span> You'll be taken
                to Cal.com to pick a time that works for you. Your name and email will be
                pre-filled automatically.
              </p>
            </div>

            <div className="mt-4 p-4 rounded-2xl bg-surface border border-black/[0.07]">
              <p className="text-xs text-muted font-semibold">
                ⚡ Limited implementation capacity per month.
              </p>
            </div>
          </div>

          {/* Right: Form */}
          <div>
            <form
              onSubmit={handleSubmit}
              className="bg-surface rounded-3xl p-8 md:p-10 border border-black/[0.07] space-y-5"
            >
              {/* Row 1 */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-foreground mb-1.5 uppercase tracking-wider">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    required
                    value={formData.full_name}
                    onChange={handleChange}
                    placeholder="Sarah Thompson"
                    className="form-input"
                    disabled={isLoading}
                    suppressHydrationWarning
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-foreground mb-1.5 uppercase tracking-wider">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="sarah@fitcoach.com"
                    className="form-input"
                    disabled={isLoading}
                    suppressHydrationWarning
                  />
                </div>
              </div>

              {/* Row 2 */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-foreground mb-1.5 uppercase tracking-wider">
                    Instagram Handle *
                  </label>
                  <input
                    type="text"
                    name="instagram_handle"
                    required
                    value={formData.instagram_handle}
                    onChange={handleChange}
                    placeholder="@sarahfitcoach"
                    className="form-input"
                    disabled={isLoading}
                    suppressHydrationWarning
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-foreground mb-1.5 uppercase tracking-wider">
                    Active Clients *
                  </label>
                  <select
                    name="active_clients"
                    required
                    value={formData.active_clients}
                    onChange={handleChange}
                    className="form-input"
                    disabled={isLoading}
                    suppressHydrationWarning
                  >
                    <option value="">Select range...</option>
                    <option value="under-15">Under 15 clients</option>
                    <option value="15-25">15–25 clients</option>
                    <option value="25-40">25–40 clients</option>
                    <option value="40+">40+ clients</option>
                  </select>
                </div>
              </div>

              {/* Row 3 */}
              <div>
                <label className="block text-xs font-bold text-foreground mb-1.5 uppercase tracking-wider">
                  Your Coaching Niche *
                </label>
                <input
                  type="text"
                  name="coaching_niche"
                  required
                  value={formData.coaching_niche}
                  onChange={handleChange}
                  placeholder="e.g. women's fat loss, muscle building, performance..."
                  className="form-input"
                  disabled={isLoading}
                  suppressHydrationWarning
                />
              </div>

              {/* Error message */}
              {error && (
                <div className="p-3 rounded-xl bg-red-50 border border-red-100 text-sm text-red-600">
                  {error}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full py-4 text-base font-bold mt-2 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Submitting...
                  </>
                ) : (
                  'Start Your 7-Day Pilot →'
                )}
              </button>

              <p className="text-center text-xs text-muted">
                You'll be redirected to Cal.com to pick your call time. No payment required.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}